import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import { getColumnOptionLabel } from 'components/lists/functions/columnPreferences';
import { ListColumnOptionType } from 'types/ListTypes';
import SecondaryButton from 'components/buttons/SecondaryButton';
import Dropdown from 'components/forms/basefields/Dropdown';
import MultiSelect from 'components/forms/basefields/MultiSelect';
import TextInput from 'components/forms/basefields/TextInput';
import DateInput from 'components/forms/basefields/DateInput';
import { AudienceCondition, AudienceFieldChoice, AudienceFieldOption, AudienceSource, LIST_OPERATORS, VALUELESS_OPERATORS } from './NewsletterTypes';

interface NewsletterAudienceBuilderProps {
    conditions: AudienceCondition[];
    onChange: (conditions: AudienceCondition[]) => void;
    errorMessage?: string;
    disabled?: boolean;
    title?: string; // Translation key of the section title
    intro?: string; // Translation key of the section intro
    source?: AudienceSource; // The contacts or the platform users, which have their own fields
}

// A selectable option of the field and operator dropdowns
interface ChoiceOption {
    value: string;
    name: string;
}

// A selectable value option with the id the multi select toggles on
interface ValueOption {
    id?: number;
    value: string | number;
    name: string;
}

// Resolves the label of an audience field, through the contact field translations like the list columns
const resolveFieldLabel = (option: AudienceFieldOption, t: (key: string, values?: Record<string, string>) => string): string => {
    const isCustomField = option.source === 'custom_field' || option.source === 'organization_custom_field' || option.source === 'admin_contact_custom_field';
    const columnType = isCustomField ? 'custom_field' : 'text';
    const label = getColumnOptionLabel('contact', { name: option.name, type: columnType, label: option.label, order_field: '' } as ListColumnOptionType);
    const resolved = isCustomField ? label : t(label);

    // The fields of the organization of a person and of the customer contact of an environment get a prefix
    if (option.group === 'organization') return t('newsletters.audience.organization_prefix', { label: resolved });
    if (option.group === 'admin_contact' && option.name !== 'has_admin_contact') return t('newsletters.audience.admin_contact_prefix', { label: resolved });
    return resolved;
};

// The audience builder of a newsletter: a list of conditions on the contact fields with a live count
const NewsletterAudienceBuilder: React.FC<NewsletterAudienceBuilderProps> = ({ conditions, onChange, errorMessage, disabled, title = 'newsletters.audience.title', intro = 'newsletters.audience.intro', source = 'contacts' }) => {
    const { t } = useTranslation();
    const [count, setCount] = useState<number | null>(null);
    const [countError, setCountError] = useState<string | null>(null);
    const [counting, setCounting] = useState(false);
    const latestRequestRef = useRef(0);

    // Fetch the fields the audience can filter on for the source
    const fieldParams = useMemo(() => ({ source }), [source]);
    const { response: fieldResponse } = useFetchData({ apiUrl: 'get_newsletter_audience_fields', params: fieldParams });
    const fields: AudienceFieldOption[] = useMemo(() => fieldResponse?.fields ?? [], [fieldResponse]);
    const fieldsByName = useMemo(() => new Map(fields.map(field => [field.name, field])), [fields]);

    // The field options of the dropdown with their resolved labels
    const fieldOptions: ChoiceOption[] = useMemo(() => fields.map(field => ({ value: field.name, name: resolveFieldLabel(field, t) })), [fields, t]);

    // Count the matching contacts a moment after the conditions change
    useEffect(() => {
        const requestId = ++latestRequestRef.current;
        setCounting(true);
        const timer = setTimeout(async () => {
            try {
                const response = await saveData({ apiUrl: 'post_newsletter_audience_count', method: 'post', data: { audience_conditions: conditions, source } });
                if (requestId !== latestRequestRef.current) return;
                setCount(response?.data?.count ?? null);
                setCountError(null);
            } catch (error: any) {
                if (requestId !== latestRequestRef.current) return;
                setCount(null);
                setCountError(error?.response?.data?.audience_conditions ?? error?.response?.data?.general ?? 'newsletters.audience.count_failed');
            } finally {
                if (requestId === latestRequestRef.current) setCounting(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [conditions, source]);

    // Returns the operator options of a field
    const operatorOptions = (field: AudienceFieldOption | undefined): ChoiceOption[] => (
        (field?.operators ?? []).map(operator => ({ value: operator, name: t(`newsletters.operators.${operator}`) }))
    );

    // Returns the value options of a field as dropdown or multi select options
    const valueOptions = (field: AudienceFieldOption | undefined): ValueOption[] => (
        (field?.options ?? []).map((choice: AudienceFieldChoice, index) => ({
            id: typeof choice.value === 'number' ? choice.value : index + 1,
            value: choice.value,
            name: choice.label,
        }))
    );

    // Replaces a condition at an index
    const updateCondition = (index: number, condition: AudienceCondition) => {
        onChange(conditions.map((current, currentIndex) => (currentIndex === index ? condition : current)));
    };

    // Adds a condition on the first field
    const addCondition = () => {
        const firstField = fields[0];
        if (!firstField) return;
        onChange([...conditions, { field: firstField.name, operator: firstField.operators[0], value: null }]);
    };

    // Removes a condition
    const removeCondition = (index: number) => {
        onChange(conditions.filter((_, currentIndex) => currentIndex !== index));
    };

    // Changes the field of a condition and resets its operator and value
    const changeField = (index: number, fieldName: string) => {
        const field = fieldsByName.get(fieldName);
        if (!field) return;
        updateCondition(index, { field: fieldName, operator: field.operators[0], value: null });
    };

    // Changes the operator of a condition and resets the value when the value shape changes
    const changeOperator = (index: number, operator: string) => {
        const condition = conditions[index];
        const wasList = LIST_OPERATORS.includes(condition.operator);
        const isList = LIST_OPERATORS.includes(operator);
        const value = VALUELESS_OPERATORS.includes(operator) || wasList !== isList ? null : condition.value;
        updateCondition(index, { ...condition, operator, value });
    };

    // Toggles a value in the list value of a condition
    const toggleListValue = (index: number, option: ValueOption) => {
        const condition = conditions[index];
        const current: (string | number)[] = Array.isArray(condition.value) ? condition.value : [];
        const exists = current.some(item => String(item) === String(option.value));
        const value = exists ? current.filter(item => String(item) !== String(option.value)) : [...current, option.value];
        updateCondition(index, { ...condition, value });
    };

    // Renders the value widget of a condition for the type of its field and its operator
    const renderValue = (index: number, condition: AudienceCondition, field: AudienceFieldOption | undefined) => {
        if (!field || VALUELESS_OPERATORS.includes(condition.operator)) return <div className='nl-condition-value nl-condition-value-empty' />;

        // A list of values from the options of the field
        if (LIST_OPERATORS.includes(condition.operator)) {
            const options = valueOptions(field);
            const selected: (string | number)[] = Array.isArray(condition.value) ? condition.value : [];
            return (
                <div className='nl-condition-value'>
                    <MultiSelect<ValueOption>
                        name={`condition-${index}-value`}
                        options={options}
                        selectedOption={options.filter(option => selected.some(item => String(item) === String(option.value)))}
                        optionFormat={(option) => option.name}
                        selectionFormat={(option) => option.name}
                        showSearch={options.length > 8}
                        sortOptions={false}
                        disabled={disabled}
                        onChange={(value) => {
                            const option = options.find(item => String(item.id) === value || String(item.value) === value);
                            if (option) toggleListValue(index, option);
                        }} />
                </div>
            );
        }

        // A single value from the options of the field
        if (field.type === 'choice' || field.type === 'checkbox') {
            const options = valueOptions(field);
            return (
                <div className='nl-condition-value'>
                    <Dropdown<ValueOption>
                        name={`condition-${index}-value`}
                        options={options}
                        backendField='value'
                        optionFormat='name'
                        showSearch={options.length > 8}
                        disabled={disabled}
                        selectedOption={options.find(option => String(option.value) === String(condition.value ?? '')) ?? null}
                        selectionFormat={(option) => option.name}
                        onChange={(value) => updateCondition(index, { ...condition, value })} />
                </div>
            );
        }

        // A date
        if (field.type === 'date') {
            return (
                <div className='nl-condition-value'>
                    <DateInput
                        name={`condition-${index}-value`}
                        value={condition.value ? new Date(condition.value) : null}
                        disabled={disabled}
                        onChange={(date) => updateCondition(index, { ...condition, value: date ? String(date).slice(0, 10) : null })} />
                </div>
            );
        }

        // A number or a text
        return (
            <div className='nl-condition-value'>
                <TextInput
                    name={`condition-${index}-value`}
                    placeholder={field.type === 'number' ? 'newsletters.audience.number_placeholder' : 'newsletters.audience.text_placeholder'}
                    value={condition.value === null || condition.value === undefined ? '' : String(condition.value)}
                    disabled={disabled}
                    onChange={(value) => updateCondition(index, { ...condition, value })} />
            </div>
        );
    };

    // Describes the count line below the conditions
    const renderCount = () => {
        if (countError) return <div className='nl-audience-count nl-audience-count-error'>{t(countError)}</div>;
        if (count === null || counting) return <div className='nl-audience-count'>{t('newsletters.audience.counting')}</div>;
        if (count === 0) return <div className='nl-audience-count nl-audience-count-warning'>{t('newsletters.audience.count_zero')}</div>;
        return <div className='nl-audience-count'>{t(source === 'users' ? 'newsletters.audience.user_count' : 'newsletters.audience.count', { count })}</div>;
    };

    return (
        <div className='nl-audience'>
            <h6 className='newsletter-card-header'>{t(title)}</h6>
            <p className='newsletter-card-intro'>{t(intro)}</p>

            {conditions.length === 0 && (
                <div className='nl-audience-empty'>{t(source === 'users' ? 'newsletters.audience.no_conditions_users' : 'newsletters.audience.no_conditions')}</div>
            )}

            {conditions.map((condition, index) => {
                const field = fieldsByName.get(condition.field);
                const operators = operatorOptions(field);
                return (
                    <div key={index} className='nl-condition-row'>
                        <div className='nl-condition-field'>
                            <Dropdown<ChoiceOption>
                                name={`condition-${index}-field`}
                                options={fieldOptions}
                                backendField='value'
                                optionFormat='name'
                                showSearch={true}
                                disabled={disabled}
                                selectedOption={fieldOptions.find(option => option.value === condition.field) ?? null}
                                selectionFormat={(option) => option.name}
                                onChange={(value) => changeField(index, value)} />
                        </div>
                        <div className='nl-condition-operator'>
                            <Dropdown<ChoiceOption>
                                name={`condition-${index}-operator`}
                                options={operators}
                                backendField='value'
                                optionFormat='name'
                                showSearch={false}
                                disabled={disabled}
                                selectedOption={operators.find(option => option.value === condition.operator) ?? null}
                                selectionFormat={(option) => option.name}
                                onChange={(value) => changeOperator(index, value)} />
                        </div>
                        {renderValue(index, condition, field)}
                        {!disabled && (
                            <button type='button' className='nl-condition-remove' title={t('general.delete')} onClick={() => removeCondition(index)}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        )}
                    </div>
                );
            })}

            {errorMessage && <div className='newsletter-field-error'>{t(errorMessage)}</div>}

            <div className='nl-audience-footer'>
                {!disabled && (
                    <SecondaryButton
                        label='newsletters.audience.add_condition'
                        size='small'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={addCondition} />
                )}
                {renderCount()}
            </div>
        </div>
    );
};

export default NewsletterAudienceBuilder;
